import { useMemo, useState } from "react"
import { motion } from "motion/react"
import { ArrowLeft, Check, Download01, Send01 } from "@untitledui/icons"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import { ApiError } from "@/lib/api/client"
import { estimatesApi } from "@/lib/api/estimates"
import { useEstimate as useEstimateQuery } from "@/lib/api/use-estimates"
import type {
  ApiEstimateLineItem,
  VehicleSection,
} from "@/lib/api/types"
import { cx } from "@/lib/utils/cx"
import { FE_REVIEW_COPY } from "./copy"

// Full Estimate — Step 7 (Review).
//
// The final result is the estimate itself. This page strips back to:
//   • a clean total card (the headline),
//   • a per-section cost breakdown (one line per section, no
//     parts/operations split),
//   • Save Draft / Send / Download PDF actions.
//
// The PDF download triggers the browser's native print dialog
// against a printable view of the estimate. The browser's "Save as
// PDF" handles file generation — no PDF library on the FE.

interface StepReviewProps {
  estimateId: number
  onBack: () => void
}

const SECTION_LABELS: Record<string, string> = {
  FRONT_BUMPER_GRILLE: "Front bumper & grille",
  REAR_BUMPER: "Rear bumper",
  FENDER: "Fender",
  FRONT_DOOR: "Front door",
  REAR_DOOR: "Rear door",
  HOOD: "Hood",
  TRUNK: "Trunk",
  ROOF: "Roof",
  FRONT_LAMPS: "Front lamps",
  REAR_LAMPS: "Rear lamps",
  QUARTER_PANEL: "Quarter panel",
  ROCKER_PANEL: "Rocker panel",
  LIFT_GATE: "Lift gate",
  TAIL_GATE: "Tail gate",
  WHEELS: "Wheels",
  FRAME: "Frame",
  TRUNK_LID: "Trunk lid",
  PICKUP_BOX: "Pickup box",
  WINDSHIELD: "Windshield",
  MISCELLANEOUS: "Other",
}

const ALL_SECTIONS: VehicleSection[] = [
  "FRONT_BUMPER_GRILLE",
  "HOOD",
  "FENDER",
  "FRONT_LAMPS",
  "FRONT_DOOR",
  "REAR_DOOR",
  "QUARTER_PANEL",
  "ROCKER_PANEL",
  "ROOF",
  "WINDSHIELD",
  "REAR_LAMPS",
  "TRUNK_LID",
  "TRUNK",
  "LIFT_GATE",
  "TAIL_GATE",
  "REAR_BUMPER",
  "WHEELS",
  "FRAME",
  "PICKUP_BOX",
]

function sectionLabel(section: string): string {
  return SECTION_LABELS[section] ?? section.replace(/_/g, " ").toLowerCase()
}

function fmtCurrency(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—"
  return `$${Math.round(n).toLocaleString()}`
}

type SubmitState =
  | { kind: "idle" }
  | { kind: "saving"; mode: "draft" | "send" }
  | { kind: "success"; mode: "draft" | "send" }
  | { kind: "error"; message: string }

interface SectionRow {
  section: string
  partsCost: number
  laborHours: number
  itemCount: number
  opCount: number
}

export function StepReview({ estimateId, onBack }: StepReviewProps) {
  const navigate = useNavigate()
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: "idle" })

  const { data: estimate } = useEstimateQuery(estimateId, {
    refetchInterval:
      submitState.kind === "idle" || submitState.kind === "saving" ? 3000 : false,
  })

  // Operations are server-owned (children of line items). The
  // review surfaces parts cost + line-item count per section;
  // labor hours come from the line item's `manHours` field which
  // the orchestrator populates.
  const sectionRows: SectionRow[] = useMemo(() => {
    if (!estimate) return []
    const lineItems = estimate.lineItems ?? []
    const map = new Map<string, { items: ApiEstimateLineItem[] }>()
    for (const li of lineItems) {
      const k = String(li.section ?? "MISCELLANEOUS")
      if (!map.has(k)) map.set(k, { items: [] })
      map.get(k)!.items.push(li)
    }
    const rows: SectionRow[] = []
    const collect = (k: string, v: { items: ApiEstimateLineItem[] }) => {
      const partsCost = v.items.reduce((sum, li) => sum + (li.avg ?? 0), 0)
      const laborHours = v.items.reduce((sum, li) => sum + (li.manHours ?? 0), 0)
      rows.push({
        section: k,
        partsCost,
        laborHours,
        itemCount: v.items.length,
        opCount: 0,
      })
    }
    for (const s of ALL_SECTIONS) {
      if (map.has(s)) {
        collect(s, map.get(s)!)
        map.delete(s)
      }
    }
    for (const [s, v] of map) collect(s, v)
    return rows
  }, [estimate])

  const totalHours = useMemo(
    () =>
      (estimate?.lineItems ?? []).reduce(
        (sum, li) => sum + (li.manHours ?? 0),
        0,
      ),
    [estimate],
  )

  const handleSubmit = async (mode: "draft" | "send") => {
    setSubmitState({ kind: "saving", mode })
    try {
      const status = mode === "draft" ? "Draft" : "Pending"
      await estimatesApi.updateStatus(estimateId, { status: status as never })
      setSubmitState({ kind: "success", mode })
      setTimeout(() => {
        if (mode === "send") {
          navigate(`/estimates/${estimateId}`)
        }
      }, 1200)
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Couldn't save"
      setSubmitState({ kind: "error", message: msg })
      toast.error(msg)
    }
  }

  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const handleDownloadPdf = async () => {
    if (downloadingPdf) return
    setDownloadingPdf(true)
    try {
      const blob = await estimatesApi.exportPdf(estimateId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `estimate-${estimateId}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Couldn't download PDF"
      toast.error(msg)
    } finally {
      setDownloadingPdf(false)
    }
  }

  // ── Loading ────────────────────────────────────────────
  if (!estimate) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-10">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="size-10 border-[3px] border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
          <p className="text-[13.5px] text-zinc-500">{FE_REVIEW_COPY.loading}</p>
        </div>
      </div>
    )
  }

  // ── Success takeover ────────────────────────────────────
  if (submitState.kind === "success") {
    return (
      <SuccessTakeover
        mode={submitState.mode}
        estimateId={estimateId}
        onPrimary={() =>
          navigate(
            submitState.mode === "send"
              ? `/estimates/${estimateId}`
              : `/estimates`,
          )
        }
        onSecondary={() => navigate("/estimates/full")}
      />
    )
  }

  const vehicleLine =
    [
      estimate.vehicle?.year,
      estimate.vehicle?.make,
      estimate.vehicle?.model,
      estimate.vehicle?.trim,
    ]
      .filter(Boolean)
      .join(" ") || "Vehicle"

  return (
    <div className="space-y-5 pb-40 sm:pb-6 print:pb-0">
      <header className="print:hidden">
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-2">
          {FE_REVIEW_COPY.eyebrow}
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {FE_REVIEW_COPY.title}
        </h2>
        <p className="mt-1.5 text-[13.5px] text-zinc-500 leading-relaxed">
          {FE_REVIEW_COPY.subtitle}
        </p>
      </header>

      {/* Hero estimate card — the headline. */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 text-white p-6 md:p-8 shadow-2xl shadow-zinc-900/20">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgb(255 255 255 / 0.6) 1px, transparent 0)",
              backgroundSize: "20px 20px",
            }}
          />
        </div>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-zinc-400 mb-2">
          Total estimate
        </p>
        <p className="text-[44px] md:text-[56px] font-bold tracking-tight tabular-nums leading-none">
          {fmtCurrency(estimate.avg)}
        </p>
        <div className="mt-3 flex items-center gap-3 text-[12.5px] text-zinc-300">
          <span className="tabular-nums">
            Range {fmtCurrency(estimate.min)} – {fmtCurrency(estimate.max)}
          </span>
          {totalHours > 0 && (
            <>
              <span className="text-zinc-500">·</span>
              <span className="tabular-nums">
                {totalHours.toFixed(1)} labor hours
              </span>
            </>
          )}
        </div>
        <p className="mt-5 text-[13px] text-zinc-300 leading-relaxed">
          <span className="font-semibold text-white">{vehicleLine}</span>
          {estimate.vehicle?.vin && (
            <span className="font-mono text-zinc-400 ml-2 text-[11.5px]">
              {estimate.vehicle.vin}
            </span>
          )}
        </p>
      </div>

      {/* Section breakdown — slim, one row per section. */}
      <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden">
        <div className="px-4 md:px-5 py-3 border-b border-zinc-100">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400">
            Breakdown by section
          </p>
        </div>
        {sectionRows.length === 0 ? (
          <div className="px-4 md:px-5 py-8 text-center text-[13px] text-zinc-500">
            {FE_REVIEW_COPY.noLineItems}
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {sectionRows.map((row) => (
              <li
                key={row.section}
                className="px-4 md:px-5 py-3 flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[13.5px] font-semibold text-zinc-900 leading-tight truncate">
                    {sectionLabel(row.section)}
                  </p>
                  <p className="mt-0.5 text-[11.5px] text-zinc-500 leading-tight tabular-nums">
                    {row.itemCount}{" "}
                    {row.itemCount === 1 ? "component" : "components"}
                    <span className="text-zinc-300 mx-1.5">·</span>
                    {row.laborHours.toFixed(1)} hr
                  </p>
                </div>
                <p className="text-[14px] font-bold tabular-nums text-zinc-900 shrink-0">
                  {fmtCurrency(row.partsCost)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Inline secondary actions — print/PDF. Stays visible on
          desktop; on mobile it sits above the sticky CTA bar. */}
      <button
        type="button"
        onClick={handleDownloadPdf}
        className={cx(
          "w-full h-12 rounded-2xl",
          "border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50",
          "transition-colors duration-150",
          "flex items-center justify-center gap-2",
          "text-[13.5px] font-semibold text-zinc-700",
          "print:hidden",
        )}
      >
        <Download01 className="size-4" aria-hidden />
        Download PDF
      </button>

      {/* CTA bar */}
      <div
        className={cx(
          "space-y-2 print:hidden",
          "fixed inset-x-0 bottom-0 z-30",
          "px-4 pt-3 pb-[max(env(safe-area-inset-bottom),12px)]",
          "bg-gradient-to-t from-white via-white/95 to-white/0",
          "sm:static sm:px-0 sm:pt-2 sm:pb-0 sm:bg-transparent",
        )}
      >
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => handleSubmit("draft")}
            disabled={submitState.kind === "saving"}
            className={cx(
              "h-12 sm:h-14 px-4 rounded-2xl text-[13.5px] sm:text-[14px] font-semibold transition-colors duration-150",
              "border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-800",
              "sm:flex-1",
              submitState.kind === "saving" && "opacity-50 cursor-not-allowed",
            )}
          >
            {submitState.kind === "saving" && submitState.mode === "draft"
              ? "Saving…"
              : FE_REVIEW_COPY.saveDraft}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit("send")}
            disabled={submitState.kind === "saving"}
            className={cx(
              "h-14 px-4 rounded-2xl text-[14px] font-semibold flex items-center justify-center gap-2 transition-all duration-150",
              "bg-zinc-900 hover:bg-black text-white shadow-lg shadow-zinc-900/15",
              "sm:flex-1",
              submitState.kind === "saving" && "opacity-50 cursor-not-allowed",
            )}
          >
            <Send01 className="size-4" aria-hidden />
            {submitState.kind === "saving" && submitState.mode === "send"
              ? "Sending…"
              : FE_REVIEW_COPY.sendEstimate}
          </button>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="w-full h-10 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {FE_REVIEW_COPY.backToOperations}
        </button>
      </div>
    </div>
  )
}

/* ── Success takeover ─────────────────────────────────────────── */

function SuccessTakeover({
  mode,
  onPrimary,
  onSecondary,
}: {
  mode: "draft" | "send"
  estimateId: number
  onPrimary: () => void
  onSecondary: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 260, damping: 28 }}
      className="text-center py-10 space-y-6"
    >
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 320,
          damping: 22,
          delay: 0.1,
        }}
        className="relative mx-auto size-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shadow-2xl shadow-emerald-600/35"
      >
        <Check className="size-10 text-white" strokeWidth={3} aria-hidden />
      </motion.div>
      <div className="space-y-2">
        <h2 className="text-[26px] md:text-[32px] font-bold text-zinc-900 tracking-tight leading-tight">
          {mode === "send" ? FE_REVIEW_COPY.successTitle : "Draft saved"}
        </h2>
        <p className="text-[14px] text-zinc-600 leading-relaxed max-w-md mx-auto">
          {mode === "send"
            ? FE_REVIEW_COPY.successBody
            : "Pick this up later from your estimates list."}
        </p>
      </div>
      <div className="flex flex-col gap-2 max-w-sm mx-auto pt-1">
        <button
          type="button"
          onClick={onPrimary}
          className="w-full h-12 rounded-xl bg-zinc-900 hover:bg-black text-white text-[14px] font-semibold transition-colors duration-150"
        >
          {FE_REVIEW_COPY.successPrimary}
        </button>
        <button
          type="button"
          onClick={onSecondary}
          className="w-full h-12 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-[14px] font-semibold text-zinc-700 transition-colors duration-150"
        >
          {FE_REVIEW_COPY.successSecondary}
        </button>
      </div>
    </motion.div>
  )
}
